<script setup lang="ts">
/**
 * Editor del presupuesto (§47).
 *
 * Es el editor del recibo con dos diferencias, y las dos vienen del documento:
 *
 *   - **Nace vacío.** No hay nada pendiente que traer — un presupuesto es
 *     anterior a la reserva, así que sus líneas salen del catálogo y de ningún
 *     otro lado.
 *   - **Tiene dos datos globales**: el período del viaje y cuántos buzos son
 *     (§54). Ninguno de los dos multiplica: la cantidad de cada renglón es el
 *     total del grupo y se carga a mano, porque en un grupo de dos uno puede
 *     hacer diez salidas y el otro cinco.
 */
import { listOfferings } from '@/api/offerings'
import { getClient } from '@/api/clients'
import type { Offering } from '@/types'
import {
  type Quote,
  type QuoteLine,
  createQuote,
  deleteQuote,
  getQuote,
  updateQuote,
} from '@/api/quotes'
import AlertBox from '@/components/AlertBox.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'

definePage({
  meta: {
    requiresAuth: true,

    // Sin item propio en el menú: se marca Quotes.
    navActiveLink: 'sales-quotes',
  },
})

const route = useRoute('sales-quotes-quote-id')
const router = useRouter()
const { currentId } = useCurrentDiveCenter()
const { formatCurrency, currencyPrefix } = usePreferences()

const quoteId = ref(String(route.params.quoteId))
const isNew = computed(() => quoteId.value === 'new')

/** Solo en el alta: de dónde sale el cliente antes de que exista el documento. */
const clientId = ref(String(route.query.clientId ?? ''))

const quote = ref<Quote | null>(null)
const offerings = ref<Offering[]>([])
const lines = ref<QuoteLine[]>([])
const notes = ref('')
const discount = ref(0)
const validUntil = ref('')
const divers = ref(1)

/**
 * El período, tal como lo escribe el picker: `'YYYY-MM-DD to YYYY-MM-DD'`, o un
 * día suelto.
 *
 * Se guarda el string del componente y se parte recién al mandar, que es la
 * lección de §47.12: mantener los dos extremos en paralelo obliga a
 * reconciliarlos con los hooks de flatpickr, y esa reconciliación es donde vivían
 * todos los síntomas.
 */
const tripRange = ref('')
const clientName = ref('')
const draftCurrency = ref('EUR')

const loading = ref(true)
const saving = ref(false)
const error = ref('')

const confirmRef = ref<InstanceType<typeof ConfirmDialog>>()

/*
 * `AppDateTimePicker` de la plantilla y no `input type="date"`: el nativo trae
 * el formato del sistema operativo y no se parece a ningún otro campo del
 * portal (§17.3 y §20.9).
 *
 * `dateFormat: 'Y-m-d'` porque es lo que viaja al backend; cómo se DIBUJA lo
 * decide flatpickr con esa misma máscara — el formato de Preferences manda en lo
 * que se imprime, no en el campo que se edita.
 */
const dateConfig = { dateFormat: 'Y-m-d' }

/** El período: un solo campo de rango, como Seasons, Closed days y Reports. */
const tripConfig = { mode: 'range' as const, showMonths: 2, dateFormat: 'Y-m-d' }

const RANGE_SEPARATOR = ' to '

/** Los dos extremos. Con un día suelto, los dos son ese día. */
const tripParts = computed(() => {
  const parts = tripRange.value.split(RANGE_SEPARATOR).map(p => p.trim()).filter(Boolean)

  return { from: parts[0] ?? '', to: parts[1] ?? parts[0] ?? '' }
})

/**
 * Cuántos días cubre, ambos extremos incluidos.
 *
 * 🪤 Sobre las cadenas y no sobre `Date`: son días sueltos, y pasarlos por `Date`
 * les inventa una hora en UTC que al oeste de Greenwich devuelve el día anterior
 * (§47.11). `Date.UTC` a partir de los tres números no tiene ese problema.
 */
const tripDays = computed(() => {
  const { from, to } = tripParts.value

  if (!from || !to)
    return 0

  const toUtc = (d: string) => {
    const [y, m, day] = d.split('-').map(Number)

    return Date.UTC(y, (m ?? 1) - 1, day ?? 1)
  }

  return Math.round((toUtc(to) - toUtc(from)) / 86400000) + 1
})

/**
 * Vence a los 7 días. Se propone acá y también lo pone el backend si falta.
 *
 * 🪤 Día LOCAL y no `toISOString()`: ese convierte a UTC, así que al oeste de
 * Greenwich —de tarde en adelante— devuelve el día siguiente, y el campo se
 * abría con una fecha corrida.
 */
function defaultValidUntil(): string {
  const d = new Date()

  d.setDate(d.getDate() + 7)

  const pad = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Cómo se le escribe un rango al picker.
 *
 * 🪤 Un día solo se escribe UNA vez: `"X to X"` es un rango para flatpickr y lo
 * repinta como tal, dejando el campo diciendo algo que nadie eligió (§47.9).
 */
function rangeString(from?: string, to?: string): string {
  if (!from)
    return ''

  return !to || to === from ? from : `${from}${RANGE_SEPARATOR}${to}`
}

const currency = computed(() => quote.value?.currency ?? draftCurrency.value)
const money = (amount: number) => formatCurrency(amount, currency.value)

/**
 * Qué se puede tocar.
 *
 * Un enviado sí: corregir un renglón y reenviar es lo normal cuando el cliente
 * pide un cambio. Lo que se cierra es un aprobado que ya generó reservas —el
 * precio de eso ya lo maneja el recibo— y un rechazado.
 */
const editable = computed(() => {
  const q = quote.value

  if (!q)
    return true
  if (q.status === 'rejected')
    return false
  if (q.status === 'approved')
    return !q.lines.some(l => l.bookingIds?.length)

  return true
})

/*
 * 🪤 **`divers` no entra en el importe.** Se evaluó que multiplicara y se
 * descartó: en un grupo de dos, uno hace diez salidas y el otro cinco, y ningún
 * producto escribe quince. La cantidad del renglón es el total del grupo.
 */
const lineAmount = (line: QuoteLine) =>
  (Number(line.unitAmount) || 0) * (Number(line.quantity) || 0)

const subtotal = computed(() =>
  Math.round(lines.value.reduce((sum, l) => sum + lineAmount(l), 0) * 100) / 100,
)

const appliedDiscount = computed(() =>
  Math.min(Math.max(Number(discount.value) || 0, 0), Math.max(subtotal.value, 0)),
)

const total = computed(() => Math.round((subtotal.value - appliedDiscount.value) * 100) / 100)

/** Los renglones que dicen algo. Es lo que se guarda y lo que habilita el envío. */
const filledLines = computed(() => lines.value.filter(l => !!l.label.trim()))

/** El catálogo del centro, para el panel de selección. */
interface OfferingOption {
  id: string
  title: string
  kind: 'service' | 'rental'
  amount: number
}

const offeringOptions = computed<OfferingOption[]>(() =>
  offerings.value
    .filter(o => o.active)
    .map(o => ({
      id: o.id,
      title: o.title,
      kind: o.kind === 'product' ? ('rental' as const) : ('service' as const),
      amount: o.price?.amount ?? 0,
    }))
    .sort((a, b) => a.title.localeCompare(b.title)),
)

async function load() {
  const dcId = currentId.value

  if (!dcId)
    return

  loading.value = true
  error.value = ''
  try {
    const offRes = await listOfferings(dcId)

    offerings.value = offRes.offerings

    if (isNew.value) {
      /*
       * Borrador nuevo, **en blanco**. Nada se escribe hasta que se guarda:
       * equivocarse de cliente y cerrar la pantalla no puede dejar un documento
       * tirado en el listado.
       */
      if (!clientId.value) {
        error.value = 'No client selected. Start the quote from the list.'

        return
      }

      const { client } = await getClient(clientId.value)

      clientName.value = `${client.firstName} ${client.lastName ?? ''}`.trim()

      // Se propone desde el vamos: un campo vacío obliga a abrir el calendario
      // para escribir la respuesta que se iba a dar igual.
      validUntil.value = defaultValidUntil()

      /*
       * Y con el primer renglón puesto. Un presupuesto nuevo SIEMPRE lleva al
       * menos uno, así que abrir con la tabla vacía y un "No lines yet" cobra un
       * click que termina siempre en el mismo lado.
       */
      addLine()

      return
    }

    const res = await getQuote(dcId, quoteId.value)

    quote.value = res.quote
    lines.value = res.quote.lines.map(l => ({ ...l }))
    notes.value = res.quote.notes ?? ''
    discount.value = res.quote.discount ?? 0
    validUntil.value = res.quote.validUntil ?? ''
    divers.value = res.quote.divers ?? 1
    tripRange.value = rangeString(res.quote.tripFrom, res.quote.tripTo)
    clientName.value = res.quote.clientName
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load this quote.')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * 🪤 **Y al cambiar el centro, que en un refresh llega DESPUÉS.**
 *
 * `currentId` se resuelve de forma asíncrona al arrancar la sesión. Entrando por
 * el listado ya está puesto, pero recargando esta URL directamente `onMounted`
 * corre con el centro todavía sin resolver: `load` se iba por el `return` de
 * arriba y la pantalla quedaba con la barra de carga puesta para siempre. Con el
 * watch, la primera resolución dispara la carga real.
 */
watch(currentId, load)

// ---- Líneas ----

function addLine() {
  lines.value.push({
    kind: 'other',
    label: '',
    unitAmount: 0,
    quantity: 1,
  })
}

function removeLine(index: number) {
  lines.value.splice(index, 1)
}

/**
 * El panel de selección de offerings, el mismo del recibo: se busca, se eligen
 * una o varias, y cada una entra como su propia línea.
 */
const pickerOpen = ref(false)
const pickerSearch = ref('')
const pickerSelected = ref<string[]>([])
const pickerRow = ref<number | null>(null)
const pickerKinds = ref<OfferingOption['kind'][]>([])

const pickerResults = computed(() => {
  const q = pickerSearch.value.trim().toLowerCase()
  const kinds = pickerKinds.value

  return offeringOptions.value.filter(o =>
    (!q || o.title.toLowerCase().includes(q))
    && (!kinds.length || kinds.includes(o.kind)))
})

/**
 * Las ofertas que el presupuesto ya lleva.
 *
 * Se deshabilitan en el panel: cargar dos veces el mismo servicio no dice nada
 * que no diga subir su cantidad, y el renglón repetido se lee como un error de
 * carga. Se muestran igual, rotuladas —esconderlas dejaría el catálogo diciendo
 * que ese servicio no existe.
 *
 * 🪤 **Menos la del renglón que se está reemplazando.** El panel abierto desde una
 * fila cambia ESA fila, así que su propia oferta tiene que seguir elegible: si no,
 * abrirlo para corregirse y arrepentirse deja el renglón sin poder volver a lo
 * que ya era.
 */
const usedOfferingIds = computed(() => {
  const row = pickerRow.value

  return new Set(
    lines.value
      .filter((l, i) => i !== row && !!l.offeringId)
      .map(l => l.offeringId!),
  )
})

function togglePickerKind(kind: OfferingOption['kind']) {
  pickerKinds.value = pickerKinds.value.includes(kind)
    ? pickerKinds.value.filter(k => k !== kind)
    : [...pickerKinds.value, kind]
}

function openPicker(index: number | null) {
  if (!editable.value)
    return

  pickerRow.value = index
  pickerSearch.value = ''
  pickerKinds.value = []
  pickerSelected.value = []
  pickerOpen.value = true
}

function togglePick(id: string) {
  if (usedOfferingIds.value.has(id))
    return

  const i = pickerSelected.value.indexOf(id)

  if (i >= 0)
    pickerSelected.value.splice(i, 1)
  else
    pickerSelected.value.push(id)
}

function lineFromOffering(o: OfferingOption): QuoteLine {
  const kind = o.kind === 'rental' ? ('rental' as const) : ('activity' as const)

  return {
    offeringId: o.id,
    kind,
    label: o.title,
    unitAmount: o.amount,

    /*
     * Un alquiler nace con los días del viaje, cuando se sabe: el equipo
     * acompaña la estadía entera, y escribir diez a mano después de haber
     * cargado el período arriba es repetir un dato que ya está.
     */
    quantity: kind === 'rental' ? Math.max(1, tripDays.value) : 1,
  }
}

function applyPicker() {
  const picked = pickerSelected.value
    .map(id => offeringOptions.value.find(o => o.id === id))
    .filter((o): o is OfferingOption => !!o)

  if (!picked.length) {
    pickerOpen.value = false

    return
  }

  const row = pickerRow.value
  const [first, ...rest] = picked

  if (row != null && lines.value[row])
    lines.value[row] = lineFromOffering(first)
  else
    lines.value.push(lineFromOffering(first))

  const at = row != null ? row + 1 : lines.value.length

  lines.value.splice(at, 0, ...rest.map(lineFromOffering))

  pickerOpen.value = false
}

// ---- Guardar ----

/**
 * Guarda. Si es un borrador nuevo lo **crea** y reemplaza la URL.
 *
 * `router.replace` y no `push`: la ruta `/new` deja de existir en cuanto el
 * documento tiene id, y dejarla en el historial haría que atrás abriera un
 * borrador vacío del mismo cliente.
 */
async function save(silent = false) {
  const dcId = currentId.value

  if (!dcId || !editable.value)
    return false

  saving.value = true
  try {
    // `amount` y `bookingId` no se mandan: el primero lo calcula el servidor y
    // el segundo lo escribe la conversión.
    /*
     * Las filas sin rótulo no se guardan: la primera viene puesta y puede quedar
     * sin llenar, y un renglón mudo en el papel se lee como un error de carga
     * (además de que el backend lo rechazaría por el rótulo vacío).
     */
    /*
     * El período va SIEMPRE, aunque esté vacío: la cadena vacía es la que le dice
     * al backend que lo borre. `undefined` no viaja en JSON, así que omitirlo
     * sería indistinguible de no haberlo tocado y dejaría el anterior puesto.
     */
    const payload = {
      lines: filledLines.value.map(({ amount, bookingIds, ...l }) => l),
      discount: appliedDiscount.value,
      notes: notes.value.trim(),
      validUntil: validUntil.value || undefined,
      divers: Math.max(1, Number(divers.value) || 1),
      tripFrom: tripParts.value.from,
      tripTo: tripParts.value.to,
    }

    if (isNew.value) {
      const { quote: created } = await createQuote(dcId, { ...payload, clientId: clientId.value })

      quote.value = created
      quoteId.value = created.id
      validUntil.value = created.validUntil ?? ''
      tripRange.value = rangeString(created.tripFrom, created.tripTo)
      await router.replace(`/sales/quotes/${created.id}`)
    }
    else {
      const { quote: saved } = await updateQuote(dcId, quoteId.value, payload)

      quote.value = saved
    }

    if (!silent)
      notifySuccess('Quote saved.')

    return true
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not save the quote.'))

    return false
  }
  finally {
    saving.value = false
  }
}

/** Descartar. Solo un borrador: uno enviado ya gastó su número, se rechaza. */
async function discard() {
  const dcId = currentId.value

  if (!dcId)
    return

  if (isNew.value) {
    router.push('/sales/quotes')

    return
  }

  const ok = await confirmRef.value?.open({
    title: 'Discard this quote?',
    message: 'It has not been sent, so nothing is lost. This cannot be undone.',
    confirmText: 'Discard',
    confirmColor: 'error',
  })

  if (!ok)
    return

  try {
    await deleteQuote(dcId, quoteId.value)
    notifySuccess('Quote discarded.')
    router.push('/sales/quotes')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not discard the quote.'))
  }
}

/** Guarda y va al preview, que es donde se envía y se decide. */
async function saveAndPreview() {
  if (!(await save(true)))
    return

  router.push(`/sales/quotes/${quoteId.value}/preview`)
}

/** El tipo de cada renglón: mismo lenguaje que el panel del catálogo. */
function lineKind(line: QuoteLine): { label: string; color: string; icon: string } {
  if (line.kind === 'rental')
    return { label: 'Rental', color: 'info', icon: 'ri-price-tag-3-line' }

  if (line.kind === 'activity')
    return { label: 'Service', color: 'primary', icon: 'ri-calendar-2-line' }

  return { label: 'Manual', color: 'secondary', icon: 'ri-pencil-line' }
}
</script>

<template>
  <div>
    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
    />

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard v-if="!loading && !error">
      <VCardItem>
        <VCardTitle class="d-flex flex-wrap align-center ga-3">
          <span>{{ quote?.number ? `Quote ${quote.number}` : 'New quote' }}</span>
          <VChip
            v-if="quote"
            size="small"
            :color="quote.displayStatus === 'rejected' ? 'error' : 'secondary'"
          >
            {{ quote.displayStatus }}
          </VChip>
          <VChip
            v-if="isNew"
            size="small"
            color="warning"
          >
            Unsaved
          </VChip>
        </VCardTitle>
        <VCardSubtitle>{{ clientName }}</VCardSubtitle>

        <template #append>
          <RouterLink to="/sales/quotes">
            <VIcon
              icon="ri-arrow-left-line"
              size="18"
              class="me-1"
            />
            All quotes
          </RouterLink>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText>
        <!--
          Los tres datos de arriba, en una grilla y no en un `d-flex`: el input de
          flatpickr es `position: absolute` y no tiene ancho propio, así que como
          flex item se encoge hasta quedar en un cuadradito vacío.
        -->
        <VRow class="mb-2">
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <!--
              Hasta cuándo vale. Nadie lo vigila: solo se imprime y tiñe la
              etiqueta del listado (§47.2).
            -->
            <AppDateTimePicker
              v-model="validUntil"
              label="Valid until"
              class="bt-label-top"
              density="compact"
              :config="dateConfig"
              :readonly="!editable"
              hint="Printed on the quote. Nothing expires automatically."
              persistent-hint
            />
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="5"
          >
            <!--
              El período del viaje: un solo campo de rango, como Seasons, Closed
              days y Reports. De acá salen los días, y es lo que siembra el rango
              del panel de conversión.
            -->
            <AppDateTimePicker
              v-model="tripRange"
              label="Trip dates"
              class="bt-label-top"
              density="compact"
              :config="tripConfig"
              :readonly="!editable"
              :hint="tripDays
                ? `${tripDays} ${tripDays === 1 ? 'day' : 'days'}`
                : 'Optional. Seeds the range when converting.'"
              persistent-hint
            />
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <!--
              Informativo: no multiplica importes ni decide plazas (§54). Quince
              salidas para uno y quince para cuatro son dos viajes distintos, y el
              que lee el papel necesita saber cuál.
            -->
            <VTextField
              v-model.number="divers"
              label="Divers"
              type="number"
              min="1"
              density="compact"
              :readonly="!editable"
              hint="Printed on the quote. It does not change any amount."
              persistent-hint
            />
          </VCol>
        </VRow>

        <!--
          Lo que hay que saber ANTES de escribir el primer número, y por eso va
          acá y no en el pie: la cantidad es del grupo entero. Con dos buzos que
          hacen distinta cantidad de salidas no hay multiplicación posible —uno
          diez y el otro cinco no son "dos por" nada— así que o se suma, o va un
          renglón por buzo.
        -->
        <VAlert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-4 text-body-2"
        >
          Quantities cover the whole group. Add one line per diver when they do
          different amounts — one may do 10 dives and another 5.
        </VAlert>

        <VTable class="text-no-wrap bt-affix-aligned">
          <thead>
            <tr>
              <th style="min-inline-size: 280px">
                Item
              </th>
              <!-- Los tres números a la derecha, como en el recibo (§46.5). -->
              <th
                class="text-end"
                style="inline-size: 140px"
              >
                Unit price
              </th>
              <!--
                "Qty" y no "Dives": el mismo número cuenta salidas en un servicio
                y jornadas o unidades en un alquiler, y un rótulo que dice una de
                las dos miente en la otra fila.
              -->
              <th
                class="text-end"
                style="inline-size: 110px"
              >
                Qty
              </th>
              <th
                class="text-end"
                style="inline-size: 140px"
              >
                Amount
              </th>
              <th style="inline-size: 48px" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(line, i) in lines"
              :key="i"
            >
              <td>
                <!-- El avatar + dos líneas de la tabla de la plantilla (§45). -->
                <div class="d-flex align-center">
                  <VTooltip
                    :text="lineKind(line).label"
                    location="top"
                  >
                    <template #activator="{ props: tip }">
                      <VAvatar
                        v-bind="tip"
                        size="32"
                        :color="lineKind(line).color"
                        variant="tonal"
                        rounded
                      >
                        <VIcon
                          :icon="lineKind(line).icon"
                          size="18"
                        />
                      </VAvatar>
                    </template>
                  </VTooltip>

                  <div class="d-flex flex-column ms-3 flex-grow-1">
                    <!--
                      Una línea ya convertida se dibuja como TEXTO: cambiarle el
                      rótulo no cambiaría la reserva que salió de ella.
                    -->
                    <span
                      v-if="!editable || line.bookingIds?.length"
                      class="d-block font-weight-medium text-high-emphasis text-truncate"
                    >{{ line.label }}</span>

                    <VTextField
                      v-else
                      v-model="line.label"
                      density="compact"
                      hide-details
                      placeholder="Description"
                      variant="underlined"
                    >
                      <template #append-inner>
                        <VBtn
                          icon="ri-shopping-bag-3-line"
                          variant="tonal"
                          color="primary"
                          size="x-small"
                          density="comfortable"
                          title="Pick from services and rentals"
                          @click="openPicker(i)"
                        />
                      </template>
                    </VTextField>

                    <small
                      v-if="line.bookingIds?.length"
                      class="text-success"
                    >Booked</small>
                  </div>
                </div>
              </td>

              <td class="text-end">
                <VTextField
                  v-model.number="line.unitAmount"
                  class="bt-num bt-num--price"
                  type="number"
                  density="compact"
                  hide-details
                  :readonly="!editable"
                  variant="underlined"
                  :prefix="currencyPrefix(currency)"
                />
              </td>

              <td class="text-end">
                <VTextField
                  v-model.number="line.quantity"
                  class="bt-num bt-num--qty"
                  type="number"
                  min="1"
                  density="compact"
                  hide-details
                  :readonly="!editable || !!line.bookingIds?.length"
                  variant="underlined"
                />
              </td>

              <td class="text-end font-weight-medium">
                {{ money(lineAmount(line)) }}
              </td>

              <td class="text-end">
                <VBtn
                  v-if="editable && !line.bookingIds?.length"
                  icon="ri-delete-bin-line"
                  variant="text"
                  size="small"
                  color="error"
                  title="Remove line"
                  @click="removeLine(i)"
                />
              </td>
            </tr>

            <tr v-if="!lines.length">
              <td
                colspan="5"
                class="text-center text-medium-emphasis py-6"
              >
                No lines yet.
              </td>
            </tr>
          </tbody>
        </VTable>

        <!--
          Un solo botón. El del catálogo suelto llevaba al mismo panel que ya
          abre el botón del renglón, así que eran dos caminos para lo mismo y uno
          más que leer.
        -->
        <VBtn
          v-if="editable"
          variant="tonal"
          prepend-icon="ri-add-line"
          class="mt-4"
          @click="addLine"
        >
          Add line
        </VBtn>

        <VDivider class="my-6" />

        <VRow>
          <VCol
            cols="12"
            md="7"
          >
            <VTextarea
              v-model="notes"
              label="Notes"
              rows="4"
              :readonly="!editable"
              hint="Printed on the quote."
              persistent-hint
            />
          </VCol>

          <VCol
            cols="12"
            md="5"
          >
            <div class="text-end">
              <div class="d-flex justify-space-between text-body-2 text-medium-emphasis my-2">
                <span>Subtotal</span>
                <span>{{ money(subtotal) }}</span>
              </div>

              <div class="d-flex align-center justify-space-between ga-3 my-2 bt-affix-aligned">
                <span class="text-body-2 text-medium-emphasis">Discount</span>
                <VTextField
                  v-model.number="discount"
                  class="bt-num bt-num--discount"
                  type="number"
                  min="0"
                  density="compact"
                  hide-details
                  variant="underlined"
                  :readonly="!editable"
                  :prefix="currencyPrefix(currency)"
                />
              </div>

              <VDivider class="my-2" />

              <div class="d-flex justify-space-between align-baseline">
                <span class="text-caption text-medium-emphasis">Total</span>
                <span class="text-h4">{{ money(total) }}</span>
              </div>
            </div>
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardActions class="px-4 py-3 d-flex flex-wrap ga-2">
        <VBtn
          v-if="!isNew"
          variant="outlined"
          prepend-icon="ri-eye-line"
          :to="`/sales/quotes/${quoteId}/preview`"
        >
          Preview
        </VBtn>

        <!-- Solo un borrador se descarta: uno enviado ya salió del centro. -->
        <VBtn
          v-if="editable && (isNew || quote?.status === 'draft')"
          variant="text"
          color="error"
          @click="discard"
        >
          Discard
        </VBtn>

        <VSpacer />

        <VBtn
          v-if="editable"
          variant="outlined"
          :loading="saving"
          @click="save"
        >
          Save
        </VBtn>

        <!--
          Guardar y pasar al preview, que es donde se envía y se registra la
          respuesta. Es el camino frecuente: se arma y se manda.
        -->
        <VBtn
          v-if="editable"
          :loading="saving"
          :disabled="!filledLines.length"
          @click="saveAndPreview"
        >
          Save and preview
        </VBtn>
      </VCardActions>
    </VCard>

    <!--
      Va en la RAÍZ de la página y no dentro de la tarjeta: un
      `VNavigationDrawer` dentro de un contexto de apilado propio queda recortado
      y con el velo puesto solo sobre ella (§24.8).
    -->
    <VNavigationDrawer
      v-model="pickerOpen"
      temporary
      location="end"
      :width="$vuetify.display.smAndDown ? undefined : 460"
      class="bt-picker"
    >
      <div class="d-flex align-center justify-space-between pa-4">
        <div>
          <div class="text-h6">
            Add from the catalogue
          </div>
          <div class="text-caption text-medium-emphasis">
            Pick one or several — each one becomes its own line.
          </div>
        </div>
        <VBtn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="pickerOpen = false"
        />
      </div>

      <VDivider />

      <div class="pa-4">
        <VTextField
          v-model="pickerSearch"
          placeholder="Search services and rentals"
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          autofocus
        />

        <div class="d-flex ga-2 mt-3">
          <VChip
            :color="pickerKinds.includes('service') ? 'primary' : undefined"
            :variant="pickerKinds.includes('service') ? 'flat' : 'outlined'"
            size="small"
            @click="togglePickerKind('service')"
          >
            Services
          </VChip>
          <VChip
            :color="pickerKinds.includes('rental') ? 'primary' : undefined"
            :variant="pickerKinds.includes('rental') ? 'flat' : 'outlined'"
            size="small"
            @click="togglePickerKind('rental')"
          >
            Rentals
          </VChip>
        </div>
      </div>

      <VList
        class="py-0"
        density="comfortable"
      >
        <VListItem
          v-for="o in pickerResults"
          :key="o.id"
          :disabled="usedOfferingIds.has(o.id)"
          @click="togglePick(o.id)"
        >
          <template #prepend>
            <VCheckboxBtn
              :model-value="pickerSelected.includes(o.id)"
              :disabled="usedOfferingIds.has(o.id)"
              color="primary"
            />
          </template>

          <VListItemTitle>{{ o.title }}</VListItemTitle>
          <VListItemSubtitle>
            <VChip
              :color="o.kind === 'rental' ? 'info' : 'primary'"
              size="x-small"
              variant="tonal"
              class="me-2"
            >
              {{ o.kind === 'rental' ? 'Rental' : 'Service' }}
            </VChip>
            <span v-if="o.amount">{{ money(o.amount) }}</span>
            <span
              v-else
              class="text-disabled"
            >No price</span>
            <!-- Por qué está apagada: sin esto se lee como un error del panel. -->
            <span
              v-if="usedOfferingIds.has(o.id)"
              class="ms-2"
            >· Already in this quote</span>
          </VListItemSubtitle>
        </VListItem>

        <VListItem v-if="!pickerResults.length">
          <VListItemTitle class="text-medium-emphasis">
            Nothing matches that.
          </VListItemTitle>
        </VListItem>
      </VList>

      <template #append>
        <VDivider />
        <div class="d-flex align-center ga-2 pa-4">
          <span class="text-caption text-medium-emphasis">
            {{ pickerSelected.length }} selected
          </span>
          <VSpacer />
          <VBtn
            variant="text"
            @click="pickerOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            :disabled="!pickerSelected.length"
            @click="applyPicker"
          >
            Add
          </VBtn>
        </div>
      </template>
    </VNavigationDrawer>

    <ConfirmDialog ref="confirmRef" />
  </div>
</template>

<style scoped>
/* 🪤 `.v-navigation-drawer__content` viene con `overflow-y: hidden` (§20.7). */
.bt-picker :deep(.v-navigation-drawer__content) {
  overflow-y: auto;
}
</style>

<style lang="scss">
/* Las mismas dos reglas del editor del recibo (§45.1 y §46.5). */
.bt-affix-aligned {
  .v-text-field__prefix,
  .v-text-field__suffix {
    align-self: stretch;
  }
}

.bt-num {
  display: inline-grid;
  vertical-align: middle;

  input {
    text-align: end;
  }
}

.bt-num--price { inline-size: 92px; }
.bt-num--qty { inline-size: 52px; }
.bt-num--discount { inline-size: 110px; }
</style>
